import React, { useCallback, useMemo } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { roleSatisfies } from '../../data/entities';
import { useCan } from '../../data/permissions';
import { apiGet, isBackendConfigured } from '../../utils/backendApi';
import useTelemetry from '../../components/command/useTelemetry';
import LiveFrame from '../live/LiveFrame';
import SecurityView from '../live/SecurityView';
import BehaviorBaseline from '../live/BehaviorBaseline';
import TrafficBoard from '../live/TrafficBoard';
import ServicesView from '../live/ServicesView';
import { APP_LIVE } from '../live/routes';
import {
  CostsSection, HealthSection, UatSection, UsageSection, useOrgDoc,
} from './AppsModule';
import './AppsModule.css';

const COPY = {
  security: ['Security', 'Assurance · scanners, runs, findings, tests, and how this app is thresholded'],
  health: ['Health', 'Uptime, latency, incidents, and feature checks'],
  services: ['Services', 'What the app is made of and how each part is doing — reported by the instance every five minutes'],
  traffic: ['Traffic', 'Edge requests, errors, and flows'],
  anomalies: ['Anomalies', 'Baselines, floors, and open flags'],
  environments: ['Environments', 'Production and UAT'],
  usage: ['Usage', 'Meters the instance reported this month'],
  costs: ['Costs', 'Spend, burn, and the month’s projection'],
};

export default function AppLivePage() {
  const { org, myRole } = useOutletContext();
  const { appId, aspect } = useParams();
  const app = useOrgDoc('appInstance', 'apps', org.id, appId);
  const isAdmin = roleSatisfies(myRole, 'admin');
  const canChecks = useCan(org, myRole)('instance.featureChecks');
  const known = APP_LIVE.includes(aspect);
  const wantsTelemetry = known && ['security', 'traffic', 'anomalies'].includes(aspect);
  const slug = app?.slug || null;
  const roster = useMemo(() => (slug ? [{ slug, twinSlug: app?.uatAppId ? `${slug}-uat` : null }] : []), [slug, app?.uatAppId]);
  const fetchReal = useCallback((w) => (isBackendConfigured() ? apiGet(`/apps/${org.id}/telemetry?window=${w}&appId=${appId}`) : Promise.resolve(null)), [org.id, appId]);
  const { telemetry, controls } = useTelemetry({ surface: 'app-live', roster, fetchReal, focusSlug: slug, enabled: wantsTelemetry && !!slug });
  const back = `/orgs/${org.id}/apps/${appId}`;
  const [title, meta] = COPY[aspect] || ['Monitoring', ''];

  if (app === undefined) {
    return <div className="cs-page live-page"><p className="cs-help">Loading…</p></div>;
  }
  if (app === null) {
    return (
      <div className="cs-page live-page">
        <p className="cs-help">App not found or you don&apos;t have access. <Link to={`/orgs/${org.id}/deployments?tab=instances`}>Back to instances</Link></p>
      </div>
    );
  }
  if (!known) {
    return (
      <LiveFrame back={back} backLabel={`← ${app.name || 'App'}`} title="Monitoring" meta="That view is not on this app.">
        <p className="cs-help">{APP_LIVE.join(' · ')}</p>
      </LiveFrame>
    );
  }

  return (
    <LiveFrame back={back} backLabel={`← ${app.name || app.slug || 'App'}`} title={title} meta={meta}>
      {aspect === 'security' && <SecurityView orgId={org.id} appId={appId} isAdmin={isAdmin} telemetry={telemetry} controls={controls} slug={slug} />}
      {aspect === 'health' && <HealthSection orgId={org.id} appId={appId} canEdit={canChecks} />}
      {aspect === 'services' && <ServicesView orgId={org.id} appId={appId} />}
      {aspect === 'traffic' && <TrafficBoard telemetry={telemetry} slug={slug} controls={controls} />}
      {aspect === 'anomalies' && <BehaviorBaseline telemetry={telemetry} slug={slug} controls={controls} />}
      {aspect === 'environments' && <UatSection org={org} appId={appId} app={app} isAdmin={isAdmin} />}
      {aspect === 'usage' && <UsageSection orgId={org.id} appId={appId} />}
      {aspect === 'costs' && (isAdmin ? <CostsSection orgId={org.id} appId={appId} /> : <p className="cs-help">Cost figures are visible to organization admins.</p>)}
    </LiveFrame>
  );
}
